/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import {useSignals} from '@preact/signals-react/runtime';
import Device from '@ion/core/src/services/Device';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import Breadcrumb from '@ion/dls/src/components/breadcrumbs/Breadcrumbs';
import {BreadcrumbItemData} from '@ion/dls/src/components/drawer/current/Breadcrumb';
import {DrawerHeader} from '@ion/dls/src/components/drawer/current/DrawerHeader';
import {SideSheetArrow} from '@ion/dls/src/components/sidesheet/SideSheetArrow';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {NavigationIcon} from '@ion/dls/src/icons/NavigationIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {BreadcrumbActions, DLSTheme} from '@ion/dls/src/types/types';
import {
  setAssetDetailView,
  setFindNearbyPopup,
  setLastSelectedAsset,
  setSelectedAsset,
  setTrackingMapIsAssetDetailHistory
} from '../../../store/actions/tracking';
import {
  findNearbySelector,
  isLargeFleetModeSelector,
  largeFleetAssetsSelector,
  mapAssetsSelector,
  queryParamsSelector,
  searchSelectedSelector,
  selectedAssetSelector,
  selectedToolSelector
} from '../../../store/selectors/tracking-selectors';
import {BreadcrumbsType} from '../../../types/sidesheet';
import changePage, {Route} from '../../RouteDefinitions';
import {
  assetDetailsAllExpanded,
  toggleAllAssetDetailsSections
} from '../assetDetailsAccordionStore';
import {useAssetDetailsCrumb} from '../hooks/breadcrumbs/useAssetDetailsCrumb';
import {useAssociatedTagsCrumb} from '../hooks/breadcrumbs/useAssociatedTagsCrumb';
import {useRootCrumb} from '../hooks/breadcrumbs/useRootCrumb';
import {useSelectedAssetFindNearByCrumb} from '../hooks/breadcrumbs/useSelectedAssetFindNearByCrumb';
import {useShowCrumbs} from '../hooks/breadcrumbs/useShowCrumb';
import {useSaveAssetViewMode} from '../hooks/sidesheet/useSaveAssetViewMode';
import {useSelectAssetAndResets} from '../hooks/sidesheet/useSelectAssetAndResets';
import useToggleSideSheet from '../hooks/tracking/useToggleSideSheet';
import {getItemValue} from '../trackingUtils';
import {SideSheetBreadCrumbLink} from './SideSheetBreadCrumbLink';

interface StyleProps {
  width?: number;
};

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    breadcrumbRoot : {
      display : 'flex',
      height  : 48
    },
    breadcrumbContainer : {
      padding                        : '0 15px 0 0',
      width                          : ({width}: StyleProps) => width,
      [theme.breakpoints.down('sm')] : {
        width : 300
      }
    },
    assetCard : {
      padding : '12px 20px 0 4px'
    },
    inactiveCrumb : {
      color  : dlsColor('grey3'),
      cursor : 'pointer'
    },
    activeCrumb : {
      color : dlsColor('grey1')
    },
    longCrumb : {
      fontSize : '9px'
    }
  };
});

interface SidesheetBreadcrumbsProps extends StyleProps {
  show?: boolean;
  showArrow?: boolean;
  route?: Route;
};

const NORMAL_LABEL_LENGTH = 24;

/**
 * @SidesheetBreadcrumbs
 * Returns wrapper component for breadcrumbs. it is responsible for creating breadcrumbs.
 *
 *  ```typescript
 *   import SidesheetBreadcrumbs from '@/routes/tracking/sidesheet/SidesheetBreadcrumbs';
 *
 *   <SidesheetBreadcrumbs />
 *  ```
 */
const SidesheetBreadcrumbs = ({
  show = false,
  showArrow = true,
  width = 308,
  route = Route.TRACKING
}: SidesheetBreadcrumbsProps) => {
  useSignals();
  const {isPhone, isDesktop}          = useMedia();
  const dispatch                      = useDispatch();
  const selectedAsset                 = useSelector(selectedAssetSelector);
  const selectedTool                  = useSelector(selectedToolSelector);
  const findLabel                     = useSelector(findNearbySelector);
  const queryParams                   = useSelector(queryParamsSelector);
  const searchSelected                = useSelector(searchSelectedSelector);
  const isLargeFleetMode              = useSelector(isLargeFleetModeSelector);
  const largeFleetAssets              = useSelector(largeFleetAssetsSelector);
  const mapAssets                     = useSelector(mapAssetsSelector);
  const [canShowCrumbs]               = useShowCrumbs();
  const [rootCrumb]                   = useRootCrumb();
  const [toolsCrumb]                  = useAssociatedTagsCrumb();
  const [detailsCrumb]                = useAssetDetailsCrumb();
  const [findNearByCrumb]             = useSelectedAssetFindNearByCrumb();
  const [viewMode, , saveViewMode]    = useSaveAssetViewMode();
  const [handleSelectedAsset, resetTracking, resetToolsAndFindNearBy, onIconClick] = useSelectAssetAndResets();
  const {
    onToggleClick,
    onCloseSideSheet
  } = useToggleSideSheet();
  const crumbs  = [rootCrumb, detailsCrumb, toolsCrumb, findNearByCrumb].filter((crumb) => !isObjectEmpty(crumb));
  const classes = useStyles({width});

  const assetList = useMemo(() => {
    if (!isLargeFleetMode && mapAssets?.length) {
      return mapAssets;
    }
    return largeFleetAssets;
  }, [largeFleetAssets, mapAssets, isLargeFleetMode]);

  useEffect(() => {
    isDesktop && saveViewMode();
  }, [isDesktop]);

  const getAssetById = useCallback(function(assetId) {
    const asset = assetList?.find((asset: Device) => asset?.getAssetValue('assetId') === assetId);
    if (asset && asset?.getAssetValue('assetId') !== selectedAsset?.getAssetValue('assetId')) {
      dispatch(setSelectedAsset({asset, coordinates: {update: true}}) as any);
    }
  }, [assetList]);

  const dispathLastSelectedAsset = useCallback(() => {
    if (selectedAsset) {
      const {assetId} = getItemValue(selectedAsset);
      const asset = assetList?.find((asset: Device) => asset?.getAssetValue('assetId') === assetId);
      dispatch(setLastSelectedAsset(asset || selectedAsset));
    }
  }, [assetList, selectedAsset]);

  const onCrumbClick = function(isCurrent: boolean) {
    if (findLabel.miles && selectedAsset && !isCurrent) {
      const {assetId} = getItemValue(selectedAsset);
      getAssetById(assetId);
    } else {
      dispathLastSelectedAsset();
    }
  };

  const onLinkClick = useCallback(function(isRoot = false) {
    if (isRoot) {
      changePage(route, null, false);
      resetToolsAndFindNearBy(false, route);
      resetTracking(true);
    } else {
      const lastCrumb = crumbs.pop();
      if (lastCrumb && !lastCrumb.click) {
        dispatch(setAssetDetailView({
          value            : true,
          forcePanelToOpen : false,
          fromTools        : !!selectedTool
        }) as any);
        resetToolsAndFindNearBy(false, route);
      } else {
        if (queryParams?.assetDetail) {
          changePage(route, null, false);
        }
        dispatch(setAssetDetailView({value: false}) as any);
        // a crumb always deselects the asset in the current theme - only the
        // back arrow keeps it selected (and its card highlighted). The legacy
        // theme keeps a searched asset selected, as before ...
        if (isCurrentTheme() || selectedAsset?.getItemValue({
          deviceAttribute : 'assetId',
          cvsAttribute    : 'assetId'
        }) !== searchSelected?.item?.assetId) {
          handleSelectedAsset(null);
        }
        dispatch(setFindNearbyPopup(false));
      }
      dispatch(setTrackingMapIsAssetDetailHistory(false));
    }
    saveViewMode();
  }, [crumbs, route]);

  const iconClick = useCallback(function() {
    saveViewMode();
    onIconClick(crumbs, route);
    dispathLastSelectedAsset();
  }, [crumbs, selectedAsset, dispathLastSelectedAsset]);

  // mirrors SideSheetBreadCrumbLink's own onLinkClick above field-for-field -
  // the new current-theme BreadcrumbItem never fires onClick for the active
  // (last) crumb by design, so isCurrent is always effectively false for
  // whichever crumb this actually reaches, same net behavior as today ...
  const breadcrumbItems: BreadcrumbItemData[] = crumbs.map(function(crumb: BreadcrumbsType, i: number) {
    return {
      label    : `${crumb.label}`,
      isActive : i === crumbs.length - 1,
      onClick  : function() {
        if (crumb?.click) {
          const isCurrent = i === crumbs.length - 1;
          onCrumbClick(isCurrent);
          onLinkClick(crumb.click !== BreadcrumbActions.backClick);
        }
      }
    };
  });

  if (canShowCrumbs || show) {
    return isCurrentTheme() ? (
      <DrawerHeader breadcrumbItems={breadcrumbItems}
        onBack={iconClick}
        onClose={onCloseSideSheet}
        isExpanded={assetDetailsAllExpanded.value}
        onToggleExpand={toggleAllAssetDetailsSections} />
    ) : (
      <div className={classes.breadcrumbRoot}>
        {
          crumbs.length > 1 && (
            <div id={'mobile-breadcrumbs'} className={classes.breadcrumbContainer}>
              <Breadcrumb icon={<NavigationIcon direction='back' />}
                height={'48px'}
                maxItems={2}
                itemsBeforeCollapse={0}
                itemsAfterCollapse={2}
                iconClick={iconClick}>
                {crumbs.map((crumb, i) => {
                  return (
                    <SideSheetBreadCrumbLink key={`crumb-${i}`}
                      onLinkClick={function(e) {
                        if (crumb?.click) {
                          e.preventDefault();
                          e.stopPropagation();
                          const isCurrent = i === crumbs.length - 1;
                          onCrumbClick(isCurrent);
                          onLinkClick(crumb.click !== BreadcrumbActions.backClick);
                        }
                      }}
                      linkLabel={`${crumb.label}`}
                      linkClass={`${(crumbs.length - 1) === i
                        ? classes.activeCrumb : classes.inactiveCrumb}
                        ${crumb.label?.length >= NORMAL_LABEL_LENGTH && classes.longCrumb}
                        `} />
                  );
                })}
              </Breadcrumb>
            </div>
          )
        }
        {!isPhone && showArrow && <SideSheetArrow onClick={onToggleClick} isSideSheetExpanded />}
      </div>
    );
  }
  return null;
};

export default React.memo(SidesheetBreadcrumbs);
